import React, { useState, useEffect } from 'react';
import { supportAPI } from '../api';

/**
 * AdminMessages — minimal "Messages" view for the founder.
 *
 * Three subtle filter chips at the top (new · replied · archived), a
 * stacked card list, and inline status actions per message. Intentionally
 * NOT a queue / ticket / dashboard. No counters that scream urgency.
 *
 * A single message expands inline to reveal the full body + status actions
 * + a "Reply via email" mailto link that pre-fills the user's address.
 */
const STATUS_FILTERS = [
  { value: 'new', label: 'New' },
  { value: 'replied', label: 'Replied' },
  { value: 'archived', label: 'Archived' },
];

const CATEGORY_LABELS = {
  workspace_help: 'Workspace help',
  board_recovery: 'Archived board recovery',
  technical:      'Technical issue',
  other:          'Other',
};

const AdminMessages = ({ onCountsChange } = {}) => {
  const [statusFilter, setStatusFilter] = useState('new');
  const [data, setData] = useState({ rows: [], counts: { new: 0, replied: 0, archived: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState({});

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await supportAPI.listAdmin(statusFilter);
      setData(res.data);
      if (typeof onCountsChange === 'function') onCountsChange(res.data.counts);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load messages.');
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusy((s) => ({ ...s, [id]: status }));
    try {
      await supportAPI.updateAdmin(id, status);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not update message.');
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[id]; return c; });
    }
  };

  return (
    <div data-testid="admin-messages" style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 980 }}>
      <header>
        <p style={kicker}>Messages</p>
        <h2 style={titleStyle}>From the people who use Maestra.</h2>
        <p style={italicCopy}>
          Read here, then reply directly from your inbox — every message includes a reply-to
          address. Mark replied or archived when you're done.
        </p>
      </header>

      <div style={chipRow} data-testid="messages-filter-row">
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              data-testid={`messages-filter-${f.value}`}
              onClick={() => setStatusFilter(f.value)}
              style={chip(active)}
            >
              {f.label}
              <span style={chipCount(active)}>{data.counts[f.value] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {error && <div data-testid="messages-error" style={errorStyle}>{error}</div>}

      {loading ? (
        <div style={{ padding: 40 }}><div className="spinner" /></div>
      ) : data.rows.length === 0 ? (
        <p data-testid="messages-empty" style={emptyState}>
          {statusFilter === 'new'
            ? 'No new messages right now.'
            : statusFilter === 'replied'
              ? 'You haven\'t replied to any messages yet.'
              : 'No archived messages.'}
        </p>
      ) : (
        <div style={listStyle}>
          {data.rows.map((m) => {
            const isOpen = openId === m.id;
            const isBusy = !!busy[m.id];
            return (
              <article
                key={m.id}
                data-testid={`message-row-${m.id}`}
                style={card(isOpen)}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : m.id)}
                  data-testid={`message-toggle-${m.id}`}
                  style={cardHead}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={cardTitle}>
                      {m.workspace_name || `@${m.user_username || 'user'}`}
                      <span style={catTag}>· {CATEGORY_LABELS[m.category] || m.category}</span>
                    </p>
                    <p style={cardMeta}>
                      @{m.user_username || '—'} · {m.user_email || '—'}
                      {m.board_name ? <> · <span style={{ fontStyle: 'italic' }}>{m.board_name}</span></> : null}
                      {' · '}{formatDate(m.created_at)}
                    </p>
                    <p style={cardPreview}>
                      {(m.message || '').slice(0, 160)}
                      {(m.message || '').length > 160 ? '…' : ''}
                    </p>
                  </div>
                </button>

                {isOpen && (
                  <div style={cardBody} data-testid={`message-body-${m.id}`}>
                    <p style={{
                      margin: 0,
                      fontFamily: 'Playfair Display, serif',
                      fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap',
                      color: 'var(--kahla-charcoal)',
                    }}>
                      {m.message}
                    </p>
                    <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
                      {m.user_email && (
                        <a
                          href={`mailto:${m.user_email}?subject=${encodeURIComponent('Re: your Maestra message')}`}
                          data-testid={`message-reply-link-${m.id}`}
                          style={primaryBtn(false)}
                        >
                          Reply by email
                        </a>
                      )}
                      {m.status !== 'replied' && (
                        <button
                          type="button"
                          data-testid={`message-mark-replied-${m.id}`}
                          onClick={() => setStatus(m.id, 'replied')}
                          disabled={isBusy}
                          style={ghostBtn(isBusy)}
                        >
                          {isBusy ? '…' : 'Mark replied'}
                        </button>
                      )}
                      {m.status !== 'archived' && (
                        <button
                          type="button"
                          data-testid={`message-archive-${m.id}`}
                          onClick={() => setStatus(m.id, 'archived')}
                          disabled={isBusy}
                          style={ghostBtn(isBusy)}
                        >
                          Archive
                        </button>
                      )}
                      {m.status === 'archived' && (
                        <button
                          type="button"
                          data-testid={`message-reopen-${m.id}`}
                          onClick={() => setStatus(m.id, 'new')}
                          disabled={isBusy}
                          style={ghostBtn(isBusy)}
                        >
                          Move back to new
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

const kicker = {
  fontFamily: 'Playfair Display, serif', fontSize: 11,
  letterSpacing: '0.32em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
};
const titleStyle = {
  fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400,
  color: 'var(--kahla-charcoal)', margin: '0 0 10px', lineHeight: 1.25,
};
const italicCopy = {
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  fontSize: 16, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65, maxWidth: 720,
};
const chipRow = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const chip = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '6px 16px',
  background: active ? 'var(--kahla-charcoal)' : 'transparent',
  color: active ? '#fff' : 'var(--kahla-charcoal)',
  border: `1px solid ${active ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
  fontFamily: 'Playfair Display, serif',
  fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
  cursor: 'pointer',
});
const chipCount = (active) => ({
  fontSize: 10,
  background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
  padding: active ? '1px 6px' : '0',
  borderRadius: 9,
  color: active ? '#fff' : 'var(--kahla-text-light)',
});
const listStyle = { display: 'flex', flexDirection: 'column', gap: 12 };
const card = (open) => ({
  background: '#fff', border: '1px solid var(--kahla-border)',
  boxShadow: open ? '0 1px 0 var(--kahla-cream)' : 'none',
});
const cardHead = {
  width: '100%', textAlign: 'left',
  display: 'block', padding: '18px 22px',
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontFamily: 'inherit',
};
const cardTitle = {
  margin: 0, fontFamily: 'Playfair Display, serif',
  fontSize: 15, fontWeight: 400, color: 'var(--kahla-charcoal)',
};
const catTag = {
  marginLeft: 10, fontSize: 11, color: 'var(--kahla-text-light)',
  fontStyle: 'italic',
};
const cardMeta = {
  margin: '4px 0 8px', fontSize: 11,
  color: 'var(--kahla-text-light)',
  letterSpacing: '0.04em',
};
const cardPreview = {
  margin: 0, fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic', fontSize: 15, lineHeight: 1.5,
  color: 'var(--kahla-charcoal)',
};
const cardBody = {
  padding: '4px 22px 22px',
  borderTop: '1px solid var(--kahla-cream)',
  marginTop: -2,
};
const primaryBtn = (disabled) => ({
  display: 'inline-block',
  background: 'var(--kahla-charcoal)', color: '#fff',
  border: 'none', padding: '8px 18px',
  fontFamily: 'inherit', fontSize: 10,
  letterSpacing: '0.22em', textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  textDecoration: 'none',
});
const ghostBtn = (disabled) => ({
  background: 'transparent', color: 'var(--kahla-charcoal)',
  border: '1px solid var(--kahla-border)',
  padding: '7px 16px',
  fontFamily: 'inherit', fontSize: 10,
  letterSpacing: '0.22em', textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});
const emptyState = {
  padding: 40, textAlign: 'center',
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  fontSize: 15, color: 'var(--kahla-text-light)',
};
const errorStyle = {
  background: '#f8eee9', color: '#7a3a3a', padding: '10px 14px',
  fontSize: 13, fontStyle: 'italic',
};

export default AdminMessages;
